import { useEffect, useState } from 'react';
import { Loader, Modal } from '@beholos/holoscommon';
import { BadgeCheck, CreditCard, Star, Trash2 } from 'lucide-react';
import {
  deletePaymentMethod,
  getPaymentMethods,
  setDefaultPaymentMethod,
  type PaymentMethod,
} from '../../services/payment.service';

interface ManagePaymentMethodsModalProps {
  isOpen: boolean;
  closeModal: () => void;
  defaultPaymentMethodId?: string | null;
  refetch: () => Promise<void>;
}

const getCardBrandLabel = (paymentMethod: PaymentMethod) => {
  return paymentMethod.issuer?.name || paymentMethod.card?.brand || paymentMethod.brand || 'Tarjeta guardada';
};

const getLastFourDigits = (paymentMethod: PaymentMethod) => {
  return paymentMethod.last_four_digits || paymentMethod.card?.last4 || paymentMethod.last4 || '----';
};

const getExpiryLabel = (paymentMethod: PaymentMethod) => {
  const month = paymentMethod.card?.exp_month || paymentMethod.exp_month;
  const year = paymentMethod.card?.exp_year || paymentMethod.exp_year;

  if (!month || !year) {
    return 'Fecha no disponible';
  }

  return `${String(month).padStart(2, '0')}/${year}`;
};

export function ManagePaymentMethodsModal({
  isOpen,
  closeModal,
  defaultPaymentMethodId,
  refetch,
}: ManagePaymentMethodsModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadMethods = async () => {
    setLoadingMethods(true);
    setError(null);

    try {
      const methods = await getPaymentMethods();
      setPaymentMethods(methods || []);
    } finally {
      setLoadingMethods(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirmDeleteId(null);
      setError(null);
      return;
    }

    loadMethods();
  }, [isOpen]);

  const handleClose = () => {
    if (loading) return;

    setConfirmDeleteId(null);
    setError(null);
    closeModal();
  };

  const refreshUserAndMethods = async () => {
    await refetch();
    await loadMethods();
  };

  const handleSetDefault = async (cardId: string) => {
    setLoading(true);
    setError(null);

    const result = await setDefaultPaymentMethod(cardId);

    if (!result) {
      setError('No se pudo actualizar la tarjeta principal. Inténtalo de nuevo.');
      setLoading(false);
      return;
    }

    await refreshUserAndMethods();
    setLoading(false);
  };

  const handleDelete = async (cardId: string) => {
    setLoading(true);
    setError(null);

    const result = await deletePaymentMethod(cardId);

    if (!result) {
      setError('No se pudo eliminar la tarjeta. Inténtalo de nuevo.');
      setLoading(false);
      return;
    }

    setConfirmDeleteId(null);
    await refreshUserAndMethods();
    setLoading(false);
  };

  const orderedPaymentMethods = [...paymentMethods].sort((a, b) => {
    if (a.id === defaultPaymentMethodId) return -1;
    if (b.id === defaultPaymentMethodId) return 1;
    return 0;
  });

  return (
    <Modal
      containerStyles="bg-white rounded-3xl shadow-lg max-w-2xl w-full mx-4 relative max-h-[90vh] flex flex-col"
      isOpen={isOpen}
      onClose={handleClose}
    >
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-2xl font-medium font-juana text-black">Métodos de pago</h3>
        <p className="text-sm text-gray-500 mt-1">
          Revisa tus tarjetas guardadas, elige cuál será la principal y elimina las que ya no uses.
        </p>
      </div>

      <div className="p-6 flex-1 overflow-y-auto bg-gray-50/60">
        {loadingMethods ? (
          <Loader />
        ) : paymentMethods.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-2xl bg-white p-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500">
              <CreditCard size={22} />
            </div>
            <div className="text-base font-medium text-black">No tienes tarjetas guardadas</div>
            <p className="text-sm text-gray-500 mt-2">
              Cuando agregues una nueva tarjeta durante un pago, aparecerá aquí.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orderedPaymentMethods.map(paymentMethod => {
              const isDefault = paymentMethod.id === defaultPaymentMethodId;
              const isDeleteLocked = isDefault || paymentMethods.length === 1;
              const isConfirmingDelete = confirmDeleteId === paymentMethod.id;

              return (
                <div
                  key={paymentMethod.id}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-colors"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-holos-light text-black">
                        <CreditCard size={22} />
                      </div>

                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-lg font-semibold capitalize text-black">
                            {getCardBrandLabel(paymentMethod)}
                          </div>
                          {isDefault && (
                            <div className="inline-flex items-center gap-1 rounded-full bg-black px-3 py-1 text-xs font-medium text-white">
                              <BadgeCheck size={14} />
                              Principal
                            </div>
                          )}
                        </div>

                        <div className="mt-1 text-sm text-gray-600">
                          Terminada en <span className="font-semibold text-black">{getLastFourDigits(paymentMethod)}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Vence {getExpiryLabel(paymentMethod)}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row md:flex-col md:items-end">
                      <button
                        type="button"
                        onClick={() => handleSetDefault(paymentMethod.id)}
                        disabled={loading || isDefault}
                        className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full border border-black px-4 py-2 text-sm text-black transition-colors hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
                      >
                        <Star size={14} />
                        {isDefault ? 'Tarjeta principal' : 'Hacer principal'}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (isConfirmingDelete) {
                            handleDelete(paymentMethod.id);
                            return;
                          }

                          setConfirmDeleteId(paymentMethod.id);
                        }}
                        disabled={loading || isDeleteLocked}
                        className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                      >
                        <Trash2 size={14} />
                        {isConfirmingDelete ? 'Confirmar eliminación' : 'Eliminar'}
                      </button>
                    </div>
                  </div>

                  {isDeleteLocked && (
                    <p className="mt-3 text-xs text-gray-500">
                      {isDefault
                        ? 'La tarjeta principal no se puede eliminar hasta que elijas otra como principal.'
                        : 'Necesitas mantener al menos una tarjeta guardada.'}
                    </p>
                  )}

                  {isConfirmingDelete && !isDeleteLocked && (
                    <p className="mt-3 text-xs text-red-600">
                      Esta acción eliminará la tarjeta guardada de tu cuenta.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-white rounded-b-3xl">
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className="cursor-pointer rounded-full border border-black px-5 py-2 text-sm text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
}